import React, { useState } from 'react';
import { type ColumnHelper } from '@tanstack/react-table';
import { Button, Icon, Tag } from '@innovationnorway/cl-react';
import { useTranslation } from 'react-i18next';
import { SivaContractOverviewDto } from 'src/api/v2';
import { formatDateToNOString } from 'src/utils/FormatValue';
import SelfDeclarationDialog from './SelfDeclarationDialog';
import { getSelfDeclarationStatus, isValidDate } from '../../utils/self-declaration';

const SelfDeclarationStatusCell: React.FC<{ row: SivaContractOverviewDto }> = ({ row }) => {
  const { t: tCommon } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);

  if (!row.organizationNumber) {
    return <Tag data-color={'neutral' as const}>{tCommon('notApplicable')}</Tag>;
  }

  const declaration = row.latestSelfDeclaration;
  const isDelivered = declaration?.selfDeclarationDelivered ?? row.selfDeclarationDelivered;
  const expiryBaseDate = declaration?.deliveredDate;

  const expiryDate = isValidDate(expiryBaseDate)
    ? (() => {
        const date = new Date(expiryBaseDate as Date);
        date.setFullYear(date.getFullYear() + 2);
        return date;
      })()
    : undefined;
  const expiryDateText = expiryDate
    ? formatDateToNOString(expiryDate, false, '{date}.{month}.{year}', true)
    : undefined;

  const status = getSelfDeclarationStatus(expiryBaseDate, isDelivered);
  const { statusText, statusColor } = (() => {
    switch (status) {
      case 'expired':
        return { statusText: tCommon('selfDeclarationExpired'), statusColor: 'danger' as const };
      case 'expiresSoon':
        return {
          statusText: expiryDateText
            ? tCommon('selfDeclarationStatusExpiresOnDate', { date: expiryDateText })
            : tCommon('expiresSoon'),
          statusColor: 'warning' as const,
        };
      case 'delivered':
        return {
          statusText: expiryDateText
            ? tCommon('selfDeclarationStatusDeliveredOnDate', { date: expiryDateText })
            : tCommon('delivered'),
          statusColor: 'success' as const,
        };
      case 'missing':
        return { statusText: tCommon('missing'), statusColor: 'danger' as const };
    }
  })();

  return (
    <>
      <div className="display--flex align-items--center gap--1">
        <Tag data-color={statusColor}>{statusText}</Tag>
        <Button
          variant="tertiary"
          aria-label={`${tCommon('edit')} ${tCommon('delivered').toLowerCase()}`}
          onClick={() => setShowDialog(true)}
        >
          <Icon prefix="fasr" iconName="pencil" />
        </Button>
      </div>

      <SelfDeclarationDialog
        show={showDialog}
        onClose={() => setShowDialog(false)}
        sivaContractId={row.id}
        organizationNumber={row.organizationNumber}
        contractNumericId={row.numericId}
        initialSelfClassification={declaration?.selfClassification ?? []}
        existingDeliveredDate={declaration?.deliveredDate}
        acceptanceDate={row.fromDate}
      />
    </>
  );
};

export const createSelfDeclarationColumn = (columnHelper: ColumnHelper<SivaContractOverviewDto>) =>
  columnHelper.accessor((row) => row.latestSelfDeclaration?.deliveredDate, {
    id: 'selfDeclaration',
    size: 190,
    minSize: 190,
    cell: (info) => <SelfDeclarationStatusCell row={info.row.original} />,
  });
